import { Component, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'multi-select-dropdown',
  templateUrl: './multi-select-dropdown.html',
  styleUrl: './multi-select-dropdown.scss',
  standalone: false
})
export class MultiSelectDropdownComponent {

  constructor(private elementRef: ElementRef) { }
  
  @Input() public type: string = "";
  @Input() public value: any;
  @Output() public valueChanged = new EventEmitter<any | null>();
  @Input() public getOptions: (type: string, search: string, maxResults?: number) => Observable<string[]>; 
  @Input() public getCustomOptions!: () => MultiSelectOption[];
  @Input() public showSearch: boolean = false;
  @Input() public width: string = "300px";
  @Input() public maxResults: number = 20;
  @Input() public getOptionsType: string | undefined = undefined;
  @Input() public allowMultiSelect: boolean = true;
  
  public visible: boolean = false;
  public search: string = "";
  public options: MultiSelectOption[] = [];
  public treeMode: boolean = false;
  public inputElement: HTMLInputElement | null = null;
  public toggleFunction: (type: string, event: MouseEvent) => void = () => { };
  public hideFunction: () => void = () => { };

  toggleDropdown(type: string, event: MouseEvent) {
    if (this.visible && this.type === type) {
      this.hide();
    } else {
      this.show(type, event);
    }
  }

  show(type: string, event: MouseEvent) {
    this.type = type;
    this.search = "";
    this.options = [];
    this.inputElement = event.target as HTMLInputElement;

    let selection = this.inputElement!.value;
    if (this.getCustomOptions) {
      setTimeout(() => {
        this.options = this.getCustomOptions ? this.getCustomOptions() : [];
        this.visible = true;
      }, 100);
    }
    else {
      this.getOptions(this.getOptionsType ?? type, "", this.maxResults)
        .subscribe((options: string[]) => {
          this.options = this.buildOptions(options, selection);
          this.visible = true;
        });
    }
  }

  hide() {
    this.visible = false;
  }

  checked(option: string): boolean {
    const foundOption = this.options.find(o => o.label === option);
    return foundOption ? foundOption.selected : false;
  }

  toggle(option: string) {
    const foundOption = this.options.find(o => o.label === option);
    if (foundOption) {
      if (!this.allowMultiSelect && !foundOption.selected) {
        this.options.forEach(currentOption => currentOption.selected = false);
      }
      foundOption.selected = !foundOption.selected;
    }
    this.inputElement!.value = this.options.filter(o => o.selected).map(o => o.label).join(";");
    const event = new Event('input', { bubbles: true });
    this.inputElement!.dispatchEvent(event);
  }

  toggleExpand(option: MultiSelectOption, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    option.expanded = !option.expanded;
    this.updateVisibility();
  }

  private searchDelayed: any = undefined;
  searchChanged() {
    clearTimeout(this.searchDelayed);
    this.searchDelayed = setTimeout(() => {
      this.getOptions(this.type, this.search, this.maxResults)
        .subscribe((options: string[]) => {
          const selection = this.inputElement!.value;
          this.options = this.buildOptions(options, selection);
        });
    }, 500);
  }

  private buildOptions(rawOptions: string[], selection: string): MultiSelectOption[] {
    const selectedLabels = selection ? selection.split(";") : [];
    const options: MultiSelectOption[] = rawOptions.map(rawOption => {
      const tabMatch = /^(\t*)(.*)$/.exec(rawOption);
      const depth = tabMatch ? tabMatch[1].length : 0;
      const label = tabMatch ? tabMatch[2] : rawOption;
      return { label, depth, selected: selectedLabels.includes(label), blockColor: "", isParent: false, expanded: false, visible: true };
    });

    const existingLabels = new Set(options.map(option => option.label));
    for (let selectedLabel of selectedLabels) {
      if (selectedLabel && !existingLabels.has(selectedLabel)) {
        options.push({ label: selectedLabel, depth: 0, selected: true, blockColor: "", isParent: false, expanded: false, visible: true });
        existingLabels.add(selectedLabel);
      }
    }

    this.treeMode = options.some(option => option.depth > 0);
    for (let i = 0; i < options.length; i++) {
      const nextOption = options[i + 1];
      options[i].isParent = !!nextOption && nextOption.depth > options[i].depth;
      // Parents are collapsed by default
      options[i].expanded = false;
    }

    this.updateVisibility(options);
    return options;
  }

  private updateVisibility(options: MultiSelectOption[] = this.options) {
    let collapsedAtDepth: number | null = null;
    for (const option of options) {
      if (collapsedAtDepth !== null && option.depth > collapsedAtDepth) {
        option.visible = false;
        continue;
      }
      collapsedAtDepth = null;
      option.visible = true;
      if (option.isParent && !option.expanded) {
        collapsedAtDepth = option.depth;
      }
    }
  }

  textChanged(event?: Event) {
    // The input can be typed into before the dropdown has ever been opened, so prefer the event target.
    const input = (event?.target as HTMLInputElement | undefined) ?? this.inputElement;
    if (!input) {
      return;
    }

    this.inputElement = input;
    this.value = input.value;
    this.valueChanged.emit(input.value);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;

    // Check if the clicked element is outside this component
    if (targetElement && !this.elementRef.nativeElement.contains(targetElement)) {
      this.hide();
    }
  }
}

export class MultiSelectOption {
  label: string = '';
  selected: boolean = false;
  blockColor: string = '';
  depth: number = 0;
  isParent: boolean = false;
  expanded: boolean = false;
  visible: boolean = true;
}